import { HDate, HebrewCalendar, flags, Sedra } from '@hebcal/core';
import { getParashaByName } from '../data/torahPortions';

/**
 * Convert number to Hebrew letters (gematria)
 * @param {number} num - Number to convert
 * @returns {string} Hebrew letters representing the number
 */
export function numberToHebrewLetters(num) {
  if (!num || num <= 0) return '';
  
  const letters = {
    1: 'א', 2: 'ב', 3: 'ג', 4: 'ד', 5: 'ה',
    6: 'ו', 7: 'ז', 8: 'ח', 9: 'ט', 10: 'י',
    20: 'כ', 30: 'ל', 40: 'מ', 50: 'נ', 60: 'ס',
    70: 'ע', 80: 'פ', 90: 'צ', 100: 'ק', 200: 'ר',
    300: 'ש', 400: 'ת'
  };
  
  // For numbers 15 and 16, use טו and טז (to avoid spelling God's name)
  // These are two characters, so geresh goes between them (like any two-character number)
  if (num === 15) return 'ט״ו';
  if (num === 16) return 'ט״ז';
  
  if (num <= 10) {
    // Single character - add apostrophe after (to the left in RTL)
    return (letters[num] || '') + '׳';
  }
  
  // Handle 11-19 specially
  if (num > 10 && num < 20) {
    if (num === 15) return 'ט״ו'; // Two characters, geresh between them
    if (num === 16) return 'ט״ז'; // Two characters, geresh between them
    const ones = num % 10;
    // Two characters (י + ones) - geresh between them
    return 'י' + '״' + (letters[ones] || '');
  }
  
  let result = '';
  let remaining = num;
  
  // Handle thousands (for years) - typically omitted in Hebrew dates
  // But we'll include it for completeness
  if (remaining >= 1000) {
    const thousands = Math.floor(remaining / 1000);
    result += letters[thousands] || '';
    remaining = remaining % 1000;
  }
  
  // Handle hundreds
  if (remaining >= 100) {
    const hundreds = Math.floor(remaining / 100);
    if (hundreds <= 4) {
      // For 1-4 hundreds, repeat ק
      result += (letters[100] || '').repeat(hundreds);
    } else if (hundreds === 5) {
      // 500 = ת (400) + ק (100)
      result += letters[400] + letters[100];
    } else if (hundreds === 6) {
      // 600 = ת (400) + ר (200)
      result += letters[400] + letters[200];
    } else if (hundreds === 7) {
      // 700 = ת (400) + ש (300)
      result += letters[400] + letters[300];
    } else if (hundreds === 8) {
      // 800 = ת (400) + ת (400)
      result += letters[400] + letters[400];
    } else if (hundreds === 9) {
      // 900 = ת (400) + ת (400) + ק (100)
      result += letters[400] + letters[400] + letters[100];
    }
    remaining = remaining % 100;
  }
  
  // Handle tens and ones with proper geresh placement
  if (remaining >= 20) {
    const tens = Math.floor(remaining / 10) * 10;
    const ones = remaining % 10;
    
    if (ones > 0) {
      // (a) Both tens and ones - geresh goes between them
      result += letters[tens] || '';
      result += '״';
      result += letters[ones] || '';
    } else {
      // (c) Only tens - single character, apostrophe after
      result += letters[tens] || '';
      result += '׳';
    }
    remaining = 0;
  } else if (remaining > 10 && remaining < 20) {
    // Handle 11-19
    if (remaining === 15) {
      result += 'ט״ו'; // Two characters, geresh between them
      remaining = 0;
    } else if (remaining === 16) {
      result += 'ט״ז'; // Two characters, geresh between them
      remaining = 0;
    } else {
      const ones = remaining % 10;
      // (a) Both tens (י=10) and ones - geresh goes between them
      result += 'י';
      result += '״';
      result += letters[ones] || '';
      remaining = 0;
    }
  } else if (remaining === 10) {
    // (c) Only tens - single character, apostrophe after
    result += letters[10] || '';
    result += '׳';
    remaining = 0;
  }
  
  if (remaining > 0) {
    // (b) Only ones - apostrophe comes AFTER it (to the left in RTL)
    result += letters[remaining] || '';
    result += '׳'; // Single apostrophe after single character
  }
  
  return result || num.toString(); // Fallback to number if conversion fails
}

/**
 * Convert year to Hebrew letters (omitting thousands, as is traditional)
 * @param {number} year - Year to convert (e.g., 5786)
 * @returns {string} Hebrew letters representing the year (e.g., תשפ״ו for 5786)
 */
export function yearToHebrewLetters(year) {
  // For Hebrew years, typically omit the thousands (5) and just show 786
  // So 5786 becomes תשפ״ו (ת=400, ש=300, פ=80, ו=6)
  // The geresh is already added in numberToHebrewLetters based on the structure
  const remainder = year % 1000;
  return numberToHebrewLetters(remainder);
}

/**
 * Hebrew month names in Hebrew characters
 */
export const HEBREW_MONTHS = {
  'Nisan': 'ניסן',
  'Iyar': 'אייר',
  'Sivan': 'סיון',
  'Tammuz': 'תמוז',
  'Av': 'אב',
  'Elul': 'אלול',
  'Tishrei': 'תשרי',
  'Cheshvan': 'חשון',
  'Kislev': 'כסלו',
  'Tevet': 'טבת',
  'Shvat': 'שבט',
  'Adar': 'אדר',
  'Adar I': 'אדר א׳',
  'Adar II': 'אדר ב׳',
  // Alternate spellings from HDate for compatibility
  'Shevat': 'שבט',
  'Sh\'vat': 'שבט',
  'Iyyar': 'אייר',
  'Tamuz': 'תמוז'
};

/**
 * Normalize month name to standardized spelling
 * Maps HDate spellings (Iyyar, Sh'vat, etc.) to standardized spellings (Iyar, Shvat, etc.)
 */
export function normalizeMonthName(monthName) {
  const monthMap = {
    'Iyyar': 'Iyar',
    'Sh\'vat': 'Shvat',
    'Shevat': 'Shvat',
    'Tamuz': 'Tammuz'
  };
  return monthMap[monthName] || monthName;
}

/**
 * Get the current Hebrew date with formatted display
 */
export function getHebrewDate() {
  const today = new HDate();
  const gregorianDate = today.greg();
  
  const day = today.getDate();
  const rawMonth = today.getMonthName('h'); // Hebrew month name in transliteration
  const month = normalizeMonthName(rawMonth); // Normalize to standardized spelling
  const monthName = normalizeMonthName(rawMonth);
  const monthHebrew = HEBREW_MONTHS[monthName] || HEBREW_MONTHS[rawMonth] || monthName; // Hebrew month name in Hebrew characters
  const year = today.getFullYear();
  
  // Convert to Hebrew letters
  const dayHebrew = numberToHebrewLetters(day);
  let yearHebrew = yearToHebrewLetters(year); // Use special year conversion (omits thousands)
  
  // Ensure geresh is part of the year string (not added separately)
  // The geresh will be added in the component, but we need to make sure the year is correct
  
  return {
    day: day,
    month: month,
    monthHebrew: monthHebrew,
    year: year,
    dayOfWeek: gregorianDate.toLocaleDateString('en-US', { weekday: 'long' }),
    formatted: `${gregorianDate.toLocaleDateString('en-US', { weekday: 'long' })}, ${day} ${month} ${year}`,
    shortFormatted: `${day} ${month} ${year}`,
    hebrewFormatted: `${dayHebrew} ${monthHebrew} ${yearHebrew}`,
    dayHebrew: dayHebrew,
    yearHebrew: yearHebrew,
  };
}

/**
 * Calculate moon phase (approximate)
 * Returns moon phase info based on days since new moon
 * Also calculates next new moon and full moon dates
 */
export function getMoonPhase() {
  const today = new Date();
  const LUNAR_CYCLE = 29.53058867; // Average lunar cycle in days
  
  // Moon phase calculation (approximate)
  // Using a known new moon date as reference
  const knownNewMoon = new Date(2024, 0, 11, 11, 57, 0); // Jan 11, 2024 11:57 UTC
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysSinceKnownNewMoon = (today - knownNewMoon) / msPerDay;
  
  // Calculate how many full cycles have passed and current age in cycle
  const cyclesSinceKnown = daysSinceKnownNewMoon / LUNAR_CYCLE;
  const ageInCycle = (cyclesSinceKnown - Math.floor(cyclesSinceKnown)) * LUNAR_CYCLE;
  
  const age = Math.floor(ageInCycle);
  
  // Determine current phase
  let phaseName, emoji;
  if (age < 2) {
    phaseName = 'New Moon';
    emoji = '🌑';
  } else if (age < 7) {
    phaseName = 'Waxing Crescent';
    emoji = '🌒';
  } else if (age < 9) {
    phaseName = 'First Quarter';
    emoji = '🌓';
  } else if (age < 14) {
    phaseName = 'Waxing Gibbous';
    emoji = '🌔';
  } else if (age < 16) {
    phaseName = 'Full Moon';
    emoji = '🌕';
  } else if (age < 21) {
    phaseName = 'Waning Gibbous';
    emoji = '🌖';
  } else if (age < 23) {
    phaseName = 'Last Quarter';
    emoji = '🌗';
  } else {
    phaseName = 'Waning Crescent';
    emoji = '🌘';
  }
  
  // Calculate next new moon and full moon dates
  const halfCycle = LUNAR_CYCLE / 2;
  
  let nextNewMoon, nextFullMoon, daysUntilNewMoon, daysUntilFullMoon;
  
  if (ageInCycle < halfCycle) {
    // Before full moon - next event is full moon, then new moon
    daysUntilFullMoon = Math.max(1, Math.ceil(halfCycle - ageInCycle));
    nextFullMoon = new Date(today.getTime() + daysUntilFullMoon * msPerDay);
    
    daysUntilNewMoon = Math.max(1, Math.ceil(LUNAR_CYCLE - ageInCycle));
    nextNewMoon = new Date(today.getTime() + daysUntilNewMoon * msPerDay);
  } else {
    // After full moon - next event is new moon, then full moon
    daysUntilNewMoon = Math.max(1, Math.ceil(LUNAR_CYCLE - ageInCycle));
    nextNewMoon = new Date(today.getTime() + daysUntilNewMoon * msPerDay);
    
    daysUntilFullMoon = Math.max(1, Math.ceil(LUNAR_CYCLE - ageInCycle + halfCycle));
    nextFullMoon = new Date(today.getTime() + daysUntilFullMoon * msPerDay);
  }
  
  // Format dates
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Determine which event is next (use ageInCycle for more accurate comparison)
  const isWaxing = ageInCycle < halfCycle;
  
  const nextEvent = isWaxing ? {
    type: 'Full Moon',
    date: nextFullMoon,
    daysUntil: daysUntilFullMoon,
    formattedDate: formatDate(nextFullMoon)
  } : {
    type: 'New Moon',
    date: nextNewMoon,
    daysUntil: daysUntilNewMoon,
    formattedDate: formatDate(nextNewMoon)
  };
  
  // Ensure nextEvent is always valid
  const result = {
    name: phaseName,
    emoji: emoji,
    description: phaseName,
    nextEvent: nextEvent
  };
  
  // Debug: log the result
  console.log('getMoonPhase result:', result);
  console.log('nextEvent exists?', !!result.nextEvent);
  console.log('nextEvent type:', result.nextEvent?.type);
  console.log('nextEvent daysUntil:', result.nextEvent?.daysUntil);
  
  return result;
}

/**
 * Get upcoming holidays and events
 */
export function getUpcomingHolidays(count = 3) {
  try {
    const today = new HDate();
    const year = today.getFullYear();
    
    // Get calendar events for the current Hebrew year
    // Note: candlelighting requires a location, so we skip it for now
    const events = HebrewCalendar.calendar({
      year: year,
      isHebrewYear: true,
    });
    
    const now = new Date();
    const upcoming = [];
    
    for (const event of events) {
      const eventDate = event.getDate().greg();
      
      // Only include future events
      if (eventDate > now) {
        const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
        
        const isRoshChodesh = (event.getFlags() & flags.ROSH_CHODESH) !== 0;
        const isMajorHoliday = (event.getFlags() & flags.MAJOR_FAST) !== 0 || 
                              (event.getFlags() & flags.MAJOR_HOLIDAY) !== 0;
        
        upcoming.push({
          name: event.render('en'),
          date: eventDate,
          daysUntil: daysUntil,
          isFullMoon: isRoshChodesh,
          isMajorHoliday: isMajorHoliday,
          hebrewDate: event.getDate().toString('h'),
        });
        
        if (upcoming.length >= count) break;
      }
    }
    
    return upcoming;
  } catch (error) {
    console.error('Error getting upcoming holidays:', error);
    return [];
  }
}

/**
 * Get countdown to next significant event (holiday or Rosh Chodesh)
 */
export function getNextEventCountdown() {
  const holidays = getUpcomingHolidays(1);
  if (holidays.length === 0) return null;
  
  return holidays[0];
}

/**
 * Get next Shabbat Shira (when Beshalach is read)
 * @returns {Object|null} Shabbat Shira info with hebrew name, english name, days until, and description
 */
export function getNextShabbatShira() {
  try {
    const today = new HDate();
    const year = today.getFullYear();
    const sedra = new Sedra(year, false);
    
    // Look ahead up to 60 weeks (more than 1 year to catch next year's Beshalach)
    for (let weeksAhead = 0; weeksAhead < 60; weeksAhead++) {
      const checkDate = new HDate(today);
      checkDate.add(weeksAhead * 7);
      
      const lookupResult = sedra.lookup(checkDate);
      if (!lookupResult || !lookupResult.parsha) continue;
      
      const parashaArray = lookupResult.parsha;
      if (!Array.isArray(parashaArray) || parashaArray.length === 0) continue;
      
      // Debug: log first few weeks to see what parasha names look like
      if (weeksAhead < 5) {
        const names = parashaArray.map(p => {
          if (typeof p.render === 'function') return p.render('en');
          return p.name || p;
        });
        console.log('Shabbat Shira check - Week', weeksAhead, 'parasha:', names);
      }
      
      // Check if Beshalach is in the parasha list
      const isBeshalach = parashaArray.some(p => {
        let name;
        if (typeof p.render === 'function') {
          name = p.render('en');
        } else if (p.name) {
          name = p.name;
        } else if (typeof p === 'string') {
          name = p;
        } else {
          name = String(p);
        }
        const nameLower = name.toLowerCase().trim();
        // Check for various spellings and formats - also check for "B'shalach" with apostrophe
        const matches = nameLower === 'beshalach' || 
               nameLower === 'beshalah' ||
               nameLower === 'b\'shalach' ||
               nameLower === "b'shalach" ||
               nameLower.includes('beshalach') ||
               nameLower.includes('beshalah') ||
               nameLower.startsWith('beshalach') ||
               nameLower.includes('b\'shalach') ||
               nameLower.includes("b'shalach");
        
        if (matches && weeksAhead < 5) {
          console.log('Found Beshalach match:', name, 'at week', weeksAhead);
        }
        
        return matches;
      });
      
      if (isBeshalach) {
        const gregorianDate = checkDate.greg();
        const now = new Date();
        const daysUntil = Math.ceil((gregorianDate - now) / (1000 * 60 * 60 * 24));
        
        console.log('Shabbat Shira found! Days until:', daysUntil, 'Date:', gregorianDate);
        
        // Only return if it's in the future
        if (daysUntil >= 0) {
          return {
            hebrewName: 'שירת הים',
            englishName: 'Shabbat Shira',
            daysUntil: daysUntil,
            date: gregorianDate,
            description: 'The Shabbat when we read the Song of the Sea, celebrating the miracle of the Red Sea splitting and the Israelites\' deliverance from Egypt.'
          };
        }
      }
    }
    
    // If we didn't find it in the current year, try next year
    const nextYear = year + 1;
    const nextYearSedra = new Sedra(nextYear, false);
    
    for (let weeksAhead = 0; weeksAhead < 20; weeksAhead++) {
      const checkDate = new HDate(nextYear, 1, 1); // Start of next Hebrew year
      checkDate.add(weeksAhead * 7);
      
      // Use the correct Sedra for the date's year
      const checkDateYear = checkDate.getFullYear();
      const sedraForDate = checkDateYear === nextYear ? nextYearSedra : new Sedra(checkDateYear, false);
      
      let lookupResult;
      try {
        lookupResult = sedraForDate.lookup(checkDate);
      } catch (e) {
        // Skip dates that cause errors (e.g., before start of year)
        continue;
      }
      if (!lookupResult || !lookupResult.parsha) continue;
      
      const parashaArray = lookupResult.parsha;
      if (!Array.isArray(parashaArray) || parashaArray.length === 0) continue;
      
      const isBeshalach = parashaArray.some(p => {
        let name;
        if (typeof p.render === 'function') {
          name = p.render('en');
        } else if (p.name) {
          name = p.name;
        } else if (typeof p === 'string') {
          name = p;
        } else {
          name = String(p);
        }
        const nameLower = name.toLowerCase().trim();
        return nameLower === 'beshalach' || 
               nameLower === 'beshalah' ||
               nameLower.includes('beshalach') ||
               nameLower.includes('beshalah') ||
               nameLower.startsWith('beshalach') ||
               nameLower.includes('b\'shalach');
      });
      
      if (isBeshalach) {
        const gregorianDate = checkDate.greg();
        const now = new Date();
        const daysUntil = Math.ceil((gregorianDate - now) / (1000 * 60 * 60 * 24));
        
        if (daysUntil >= 0) {
          return {
            hebrewName: 'שירת הים',
            englishName: 'Shabbat Shira',
            daysUntil: daysUntil,
            date: gregorianDate,
            description: 'The Shabbat when we read the Song of the Sea, celebrating the miracle of the Red Sea splitting and the Israelites\' deliverance from Egypt.'
          };
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting Shabbat Shira:', error);
    return null;
  }
}

/**
 * Get current week's Torah portion (Parasha) and Haftarah
 * @returns {Object|null} Current parasha with haftarah, or null if no regular reading (e.g., during holiday)
 */
export function getCurrentWeeklyReading() {
  try {
    const hdate = new HDate();
    const year = hdate.getFullYear();
    const sedra = new Sedra(year, false); // false = diaspora (not in Israel)
    
    // Get the portions for this week
    // sedra.lookup() returns an object with a 'parsha' property containing an array
    const lookupResult = sedra.lookup(hdate);
    
    // Check if lookup result is valid
    if (!lookupResult || !lookupResult.parsha) {
      return null;
    }
    
    // Get the parasha array from the lookup result
    const parashaArray = lookupResult.parsha;
    
    if (!Array.isArray(parashaArray) || parashaArray.length === 0) {
      // No regular portion this week (likely a holiday Shabbat)
      return null;
    }
    
    // Get the first portion name (English)
    // Sometimes there are double portions, we'll use the first one
    const firstParasha = parashaArray[0];
    
    if (!firstParasha) {
      return null;
    }
    
    // Extract the portion name
    let parashaName;
    if (typeof firstParasha.render === 'function') {
      parashaName = firstParasha.render('en');
    } else if (firstParasha.name) {
      parashaName = firstParasha.name;
    } else if (typeof firstParasha === 'string') {
      parashaName = firstParasha;
    } else {
      return null;
    }
    
    // Look up in our database
    // Handle variations in naming (e.g., "Vayakhel-Pekudei" vs "Vayakhel")
    const parashaId = parashaName.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    
    // Try to find by ID first
    let parashaData = getParashaByName(parashaId.split('-')[0]); // Try first part if double
    
    // If not found, try by name
    if (!parashaData) {
      parashaData = getParashaByName(parashaName);
    }
    
    // If still not found, return basic info
    if (!parashaData) {
      return {
        id: parashaId,
        hebrewName: parashaName,
        englishName: parashaName,
        book: 'Unknown',
        mainIdea: 'Weekly Torah reading',
        story: `This week we read ${parashaName} from the Torah.`,
        haftarah: {
          source: 'See local synagogue',
          hebrewName: '',
          mainIdea: 'Weekly prophetic reading',
          story: 'Check with your local community for this week\'s haftarah reading.'
        }
      };
    }
    
    return parashaData;
  } catch (error) {
    console.error('Error getting current weekly reading:', error);
    return null;
  }
}

/**
 * Get the moon phase image filename based on Hebrew day
 * Mapping:
 * - Day 1 = moonphase-01.webp (new moon)
 * - Day 15 = moonphase-16.webp (full moon)
 * - Last day of month = moonphase-31.webp
 * 
 * We skip moonphase-12, moonphase-20, and moonphase-27 to ensure proper mapping
 * 
 * @param {number} hebrewDay - The day of the Hebrew month (1-30)
 * @param {number} daysInMonth - The total number of days in the current Hebrew month
 * @returns {string} Path to the moon phase image
 */
export function getMoonPhaseImage(hebrewDay, daysInMonth) {
  // For the last day of the month, always use moonphase-31.webp
  if (hebrewDay === daysInMonth) {
    return `/moonphases-webp/moonphase-31.webp`;
  }
  
  // Map day to moonphase number
  let moonPhaseNum;
  if (hebrewDay <= 11) {
    // Days 1-11: moonphase-01 to moonphase-11
    moonPhaseNum = hebrewDay;
  } else if (hebrewDay === 12) {
    // Day 12: skip moonphase-12, use moonphase-13
    moonPhaseNum = 13;
  } else if (hebrewDay <= 14) {
    // Days 13-14: moonphase-14 to moonphase-15
    moonPhaseNum = hebrewDay;
  } else if (hebrewDay === 15) {
    // Day 15: moonphase-16 (full moon)
    moonPhaseNum = 16;
  } else if (hebrewDay <= 18) {
    // Days 16-18: moonphase-17 to moonphase-19
    moonPhaseNum = hebrewDay + 1;
  } else if (hebrewDay === 19) {
    // Day 19: use moonphase-19 (before skipping moonphase-20)
    moonPhaseNum = 19;
  } else if (hebrewDay === 20) {
    // Day 20: skip moonphase-20, use moonphase-21
    moonPhaseNum = 21;
  } else if (hebrewDay <= 24) {
    // Days 21-24: moonphase-22 to moonphase-25
    moonPhaseNum = hebrewDay + 1;
  } else if (hebrewDay === 25) {
    // Day 25: skip moonphase-27, use moonphase-28
    moonPhaseNum = 28;
  } else {
    // Days 26-29: moonphase-29, moonphase-30, moonphase-31
    moonPhaseNum = hebrewDay + 3;
    if (moonPhaseNum > 31) moonPhaseNum = 31;
  }
  
  // Ensure we don't exceed 31
  moonPhaseNum = Math.min(moonPhaseNum, 31);
  
  // Format with leading zero
  const paddedNum = moonPhaseNum.toString().padStart(2, '0');
  return `/moonphases-webp/moonphase-${paddedNum}.webp`;
}

